import { Injectable, Signal, computed, inject } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';

import { SessionService } from './session.service';
import { StorageService } from './storage.service';
import { StorageKey, userPrefKey } from './storage-keys';
import { userPreference } from './user-preference';

const NONE: readonly string[] = Object.freeze([]);

/** How many projects fit in the selector's bar as chips. */
export const MAX_PINNED_PROJECTS = 3;

/**
 * The navbar project selector's two preferences: the full checked-project set (0, 1, or N ids),
 * not just the single "active" project `ProjectService` tracks, and which projects are pinned to
 * the selector's bar, in order. `ProjectSelectorComponent` writes both; pages that need to react
 * to multi-selection (e.g. the Sites page's per-project tables) read here instead of duplicating
 * the selector's own bookkeeping. An empty selection means All Projects.
 *
 * Both are scoped by user, so neither can be read before the session has arrived — hence the
 * empty set (All Projects) the whole boot window sees. Nothing writes a default over it, so the
 * real choice simply replaces it once the session lands.
 */
@Injectable({
  providedIn: 'root',
})
export class ProjectSelectionService {
  private readonly storage = inject(StorageService);
  private readonly session = inject(SessionService);

  private readonly selected = this.preference(userPrefKey.selectedProjects);
  private readonly pinned = this.preference(userPrefKey.pinnedProjects);

  readonly selectedProjectIds: Signal<ReadonlySet<string>> = computed(() => new Set(this.selected.value()));
  readonly pinnedProjectIds: Signal<ReadonlySet<string>> = computed(() => new Set(this.pinned.value()));
  /** The pins in the order the bar shows them, which the reader sets by dragging them around. */
  readonly pinnedProjectOrder: Signal<readonly string[]> = this.pinned.value;

  readonly selectedProjectIds$ = toObservable(this.selectedProjectIds);

  setSelectedProjectIds(ids: Iterable<string>): void {
    this.selected.set([...ids]);
  }

  setPinnedProjectIds(ids: Iterable<string>): void {
    this.pinned.set([...ids]);
  }

  private preference(key: (userId: string) => StorageKey) {
    return userPreference<readonly string[]>({
      userId: this.session.userId,
      read: (userId) => this.storage.getStringArray(key(userId)),
      write: (userId, ids) => this.storage.setJson(key(userId), ids),
      empty: NONE,
    });
  }
}
